const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * API Client for Moniva
 *
 * SECURITY NOTE: All API endpoints now require authentication via session cookies.
 * The userId is no longer passed from the client - it comes from the authenticated session.
 *
 * Authentication is handled automatically via the 'credentials: include' option
 * which sends session cookies with each request.
 */

export interface Database {
  id: string;
  userId: string;
  name: string;
  type: 'postgres' | 'mysql' | 'sqlite';
  host?: string;
  port?: number;
  database: string;
  username?: string;
  ssl?: boolean;
  filePath?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SavedQuery {
  id: string;
  userId: string;
  databaseId: string;
  name: string;
  query: string;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface QueryHistory {
  id: string;
  databaseId: string;
  userId: string;
  query: string;
  executionTime: number;
  rowCount: number;
  status: 'success' | 'error';
  error?: string;
  createdAt: Date;
}

export interface QueryResult {
  data: any[];
  rowCount: number;
  fields: { name: string; dataType: number }[];
  executionTime: number;
}

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'Request failed',
      }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async testConnection(data: {
    type: 'postgres' | 'mysql' | 'sqlite';
    host?: string;
    port?: number;
    database: string;
    username?: string;
    password?: string;
    ssl?: boolean;
    filePath?: string;
  }): Promise<{ success: boolean; message: string; details?: any }> {
    return this.request('/api/databases/test-connection', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async connectDatabase(data: {
    name: string;
    type: 'postgres' | 'mysql' | 'sqlite';
    host?: string;
    port?: number;
    database: string;
    username?: string;
    password?: string;
    ssl?: boolean;
    filePath?: string;
  }): Promise<{ success: boolean; database: Database }> {
    return this.request('/api/databases/connect', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listDatabases(): Promise<{ databases: Database[] }> {
    return this.request('/api/databases/list');
  }

  async updateDatabaseName(id: string, name: string): Promise<{ success: boolean; database: Database }> {
    return this.request(`/api/databases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  async deleteDatabase(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/databases/${id}`, {
      method: 'DELETE',
    });
  }

  async executeQuery(
    databaseId: string,
    query: string,
    params: any[] = []
  ): Promise<{ success: boolean } & QueryResult> {
    return this.request(`/api/databases/${databaseId}/query`, {
      method: 'POST',
      body: JSON.stringify({ query, params }),
    });
  }

  async getTables(databaseId: string): Promise<{ tables: string[] }> {
    return this.request(`/api/databases/${databaseId}/tables`);
  }

  async getTableSchema(
    databaseId: string,
    tableName: string
  ): Promise<{ schema: any[] }> {
    return this.request(`/api/databases/${databaseId}/tables/${tableName}/schema`);
  }

  async getTableData(
    databaseId: string,
    tableName: string,
    options: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
      filters?: { column: string; operator: string; value: string }[];
    } = {}
  ): Promise<{ rows: any[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.filters && options.filters.length > 0) {
      params.append('filters', JSON.stringify(options.filters));
    }

    return this.request(
      `/api/databases/${databaseId}/tables/${tableName}/data?${params.toString()}`
    );
  }

  async getDatabaseSchema(databaseId: string): Promise<{ tables: { name: string; fields: any[] }[] }> {
    return this.request(`/api/databases/${databaseId}/schema`);
  }

  async getSchema(databaseId: string): Promise<{ schema: { tables: { name: string; fields: any[] }[] } }> {
    return this.request(`/api/databases/${databaseId}/schema`);
  }

  async saveQuery(data: {
    name: string;
    query: string;
    databaseId: string;
    description?: string;
  }): Promise<{ success: boolean; query: SavedQuery }> {
    return this.request('/api/databases/queries/save', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSavedQueries(
    databaseId?: string
  ): Promise<{ queries: SavedQuery[] }> {
    const params = new URLSearchParams();
    if (databaseId) params.append('databaseId', databaseId);
    const queryString = params.toString();
    return this.request(`/api/databases/queries/saved${queryString ? '?' + queryString : ''}`);
  }

  async deleteSavedQuery(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/databases/queries/${id}`, {
      method: 'DELETE',
    });
  }

  async getQueryHistory(
    databaseId?: string,
    limit: number = 50
  ): Promise<{ history: QueryHistory[] }> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (databaseId) params.append('databaseId', databaseId);
    return this.request(`/api/databases/queries/history?${params.toString()}`);
  }

  // Analytics API
  async getAnalyticsStats(): Promise<{
    totalQueries: number;
    avgExecutionTime: number;
    successRate: number;
    totalDatabases: number;
    queriesThisWeek: number;
    slowestQuery: number;
  }> {
    return this.request('/api/analytics/stats');
  }

  async getRecentActivity(limit: number = 10): Promise<{ activity: any[] }> {
    return this.request(`/api/analytics/recent-activity?limit=${limit}`);
  }

  async getQueryPerformance(days: number = 30): Promise<{ performance: any[] }> {
    return this.request(`/api/analytics/query-performance?days=${days}`);
  }

  async getQueryVolume(days: number = 30): Promise<{ volume: any[] }> {
    return this.request(`/api/analytics/query-volume?days=${days}`);
  }

  // Settings API
  async getProfile(): Promise<{ profile: any }> {
    return this.request('/api/settings/profile');
  }

  async updateProfile(data: { name?: string; image?: string }): Promise<{ success: boolean; profile: any }> {
    return this.request('/api/settings/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getPreferences(): Promise<{ preferences: any }> {
    return this.request('/api/settings/preferences');
  }

  async updatePreferences(preferences: any): Promise<{ success: boolean; preferences: any }> {
    return this.request('/api/settings/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
  }

  // Team Management API
  async getUserOrganizations(): Promise<{ organizations: any[] }> {
    return this.request('/api/team/organizations');
  }

  async getTeamMembers(organizationId: string): Promise<{ members: any[] }> {
    return this.request(`/api/team/members?organizationId=${organizationId}`);
  }

  async updateMemberRole(memberId: string, role: string): Promise<{ success: boolean; member: any }> {
    return this.request(`/api/team/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async removeMember(memberId: string): Promise<{ success: boolean }> {
    return this.request(`/api/team/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async getInvitations(organizationId: string): Promise<{ invitations: any[] }> {
    return this.request(`/api/team/invitations?organizationId=${organizationId}`);
  }

  async createInvitation(data: {
    organizationId: string;
    email: string;
    role: string;
    invitedBy: string;
  }): Promise<{ success: boolean; invitation: any }> {
    return this.request('/api/team/invitations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resendInvitation(invitationId: string): Promise<{ success: boolean }> {
    return this.request(`/api/team/invitations/${invitationId}/resend`, {
      method: 'POST',
    });
  }

  async revokeInvitation(invitationId: string): Promise<{ success: boolean }> {
    return this.request(`/api/team/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  async getRoles(): Promise<{ roles: any[] }> {
    return this.request('/api/team/roles');
  }

  // Security API
  async getApiKeys(): Promise<{ apiKeys: any[] }> {
    return this.request('/api/security/api-keys');
  }

  async createApiKey(data: {
    name: string;
    permissions: string[];
    expiresAt?: string;
  }): Promise<{ success: boolean; apiKey: any }> {
    return this.request('/api/security/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeApiKey(keyId: string): Promise<{ success: boolean }> {
    return this.request(`/api/security/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  async getAccessLogs(limit: number = 50, days: number = 7): Promise<{ logs: any[] }> {
    return this.request(`/api/security/access-logs?limit=${limit}&days=${days}`);
  }

  async getAuditTrail(params: {
    organizationId?: string;
    category?: string;
    limit?: number;
    days?: number;
  }): Promise<{ entries: any[] }> {
    const searchParams = new URLSearchParams();
    if (params.organizationId) searchParams.append('organizationId', params.organizationId);
    if (params.category) searchParams.append('category', params.category);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.days) searchParams.append('days', params.days.toString());

    return this.request(`/api/security/audit-trail?${searchParams.toString()}`);
  }

  async generateSQL(databaseId: string, prompt: string): Promise<{ sql: string; explanation: string }> {
    return this.request('/api/ai/generate-sql', {
      method: 'POST',
      body: JSON.stringify({ databaseId, prompt }),
    });
  }

  async explainQuery(databaseId: string, sql: string): Promise<{ explanation: string; complexity: string }> {
    return this.request('/api/ai/explain-query', {
      method: 'POST',
      body: JSON.stringify({ databaseId, sql }),
    });
  }

  async optimizeQuery(databaseId: string, sql: string, executionTime: number): Promise<{
    suggestions: string[];
    optimizedSQL?: string;
    estimatedImprovement?: string;
  }> {
    return this.request('/api/ai/optimize-query', {
      method: 'POST',
      body: JSON.stringify({ databaseId, sql, executionTime }),
    });
  }

  async explainError(databaseId: string, sql: string, error: string): Promise<{
    explanation: string;
    fix: string;
    preventionTips: string[];
  }> {
    return this.request('/api/ai/explain-error', {
      method: 'POST',
      body: JSON.stringify({ databaseId, sql, error }),
    });
  }

  async analyzeResults(databaseId: string, sql: string, results: any[]): Promise<{
    insights: string[];
    anomalies: string[];
    suggestedActions: string[];
  }> {
    return this.request('/api/ai/analyze-results', {
      method: 'POST',
      body: JSON.stringify({ databaseId, sql, results }),
    });
  }

  async generateDocumentation(databaseId: string): Promise<{ markdown: string }> {
    return this.request('/api/ai/generate-docs', {
      method: 'POST',
      body: JSON.stringify({ databaseId }),
    });
  }

  async sendFeedback(data: {
    userId?: string;
    userName: string;
    userEmail: string;
    feedback: string;
    image?: string | null;
  }): Promise<{ success: boolean; message: string }> {
    return this.request('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async answerSchemaQuestion(databaseId: string, question: string): Promise<{ answer: string }> {
    return this.request('/api/ai/answer-schema-question', {
      method: 'POST',
      body: JSON.stringify({ databaseId, question }),
    });
  }

  // Query Safety Features
  async analyzeQuery(databaseId: string, query: string): Promise<any> {
    return this.request(`/api/databases/${databaseId}/query/analyze`, {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  }

  async testQueryInSandbox(databaseId: string, query: string): Promise<any> {
    return this.request(`/api/databases/${databaseId}/query/sandbox`, {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  }

  // Schema Management
  async getSchemas(organizationId?: string): Promise<{ success: boolean; schemas: any[] }> {
    const params = organizationId ? `?organizationId=${organizationId}` : '';
    return this.request(`/api/schemas${params}`);
  }

  async getSchema(id: string): Promise<{ success: boolean; schema: any }> {
    return this.request(`/api/schemas/${id}`);
  }

  async createSchema(data: {
    name: string;
    description?: string;
    nodes: any[];
    edges: any[];
    databaseId?: string;
    organizationId?: string;
    isPublic?: boolean;
  }): Promise<{ success: boolean; schema: any }> {
    return this.request('/api/schemas', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSchema(id: string, data: {
    name?: string;
    description?: string;
    nodes?: any[];
    edges?: any[];
    databaseId?: string;
    isPublic?: boolean;
  }): Promise<{ success: boolean; schema: any }> {
    return this.request(`/api/schemas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSchema(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/schemas/${id}`, {
      method: 'DELETE',
    });
  }

  async duplicateSchema(id: string): Promise<{ success: boolean; schema: any }> {
    return this.request(`/api/schemas/${id}/duplicate`, {
      method: 'POST',
    });
  }

  // AI Schema Designer
  async generateSchemaDesign(prompt: string, currentSchema?: any): Promise<{ tables: any[]; explanation: string }> {
    return this.request('/api/ai/generate-schema-design', {
      method: 'POST',
      body: JSON.stringify({ prompt, currentSchema }),
    });
  }

  // Public Templates
  async publishSchema(data: {
    schemaId: string;
    title: string;
    description?: string;
    categories: string[];
  }): Promise<{ success: boolean; template: any }> {
    return this.request('/api/templates/publish', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPublicTemplates(params?: {
    category?: string;
    search?: string;
    sortBy?: 'recent' | 'popular' | 'mostDuplicated';
    limit?: number;
    offset?: number;
  }): Promise<{ templates: any[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    return this.request(`/api/templates${query ? `?${query}` : ''}`);
  }

  async getTemplateById(id: string): Promise<{ template: any; schema: any }> {
    return this.request(`/api/templates/${id}`);
  }

  async duplicateTemplate(id: string): Promise<{ success: boolean; schema: any }> {
    return this.request(`/api/templates/${id}/duplicate`, {
      method: 'POST',
    });
  }

  async incrementTemplateView(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/templates/${id}/view`, {
      method: 'POST',
    });
  }

  async unpublishTemplate(templateId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  async getSubscription(): Promise<{ subscription: any }> {
    return this.request('/api/billing/subscription');
  }

  async getUsageStats(): Promise<{ usage: any }> {
    return this.request('/api/billing/usage');
  }

  async getBillingPlans(): Promise<{ plans: any[] }> {
    return this.request('/api/billing/plans');
  }
}

export const api = new ApiClient();
